import * as pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { Contract, Person, LegalEntity } from '../types/Contract';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

// Подключаем шрифты
(pdfMake as any).vfs = pdfFonts;

// Функция для определения мобильного устройства
const isMobileDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;

  return isMobile || (isTouch && isSmallScreen);
};

// Функция для определения iOS устройства
const isIOSDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/i.test(userAgent);
};

// Функция для автоматической печати PDF
const printPDFOnMobile = (pdfDocDefinition: any, fileName: string): void => {
  const pdfDocGenerator = (pdfMake as any).createPdf(pdfDocDefinition);

  if (isIOSDevice()) {
    // Для iOS устройств создаем blob и открываем в новом окне с автопечатью
    pdfDocGenerator.getBlob((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.addEventListener('load', () => {
          // Небольшая задержка для полной загрузки PDF
          setTimeout(() => {
            printWindow.print();
          }, 1000);
        });

        // Fallback для случаев когда load событие не сработает
        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            printWindow.print();
          }
        }, 2000);
      } else {
        // Если окно не открылось, показываем уведомление
        window.alert('Для печати разрешите всплывающие окна в браузере');
        // Скачиваем как fallback
        pdfDocGenerator.download(fileName);
      }
    });
  } else {
    // Для Android и других мобильных устройств
    pdfDocGenerator.getBase64((data: string) => {
      const linkSource = `data:application/pdf;base64,${data}`;
      const printWindow = window.open(linkSource, '_blank');

      if (printWindow) {
        printWindow.addEventListener('load', () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        });

        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            printWindow.print();
          }
        }, 1500);
      } else {
        // Fallback - создаем ссылку для скачивания с автокликом
        const downloadLink = document.createElement('a');
        downloadLink.href = linkSource;
        downloadLink.download = fileName;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        // Показываем уведомление пользователю
        setTimeout(() => {
          if (window.confirm('PDF загружен. Открыть диалог печати?')) {
            window.print();
          }
        }, 500);
      }
    });
  }
};

// Экспортируем функции для возможного использования в других компонентах
export { isMobileDevice, isIOSDevice };

// Функция для принудительного скачивания PDF (независимо от типа устройства)
export const downloadContractPDF = (contract: Contract) => {
  const { docDefinition, fileName } = createDocumentDefinition(contract);
  (pdfMake as any).createPdf(docDefinition).download(fileName);
};

// Функция для принудительной печати PDF (независимо от типа устройства)
// Функция для генерации описания стороны договора
const getPartyDescription = (party: Person | LegalEntity, role: 'seller' | 'buyer'): string => {
  if ((party as any).type === 'legal_entity') {
    const legal = party as LegalEntity;
    const roleName = role === 'seller' ? 'Продавец' : 'Покупатель';
    const kppText = legal.kpp ? `, КПП ${legal.kpp}` : '';
    return `${legal.name}, ИНН ${legal.inn}${kppText}, ОГРН ${legal.ogrn}, юридический адрес: ${legal.legalAddress}, в лице ${legal.directorPosition} ${legal.directorName}, действующего на основании ${legal.actingBasis}, именуемое в дальнейшем "${roleName}", с ${role === 'seller' ? 'одной' : 'другой'} стороны`;
  }

  const person = party as Person;
  const roleName = role === 'seller' ? 'Продавец' : 'Покупатель';

  // Если это продавец и действует по доверенности
  if (role === 'seller' && person.isActingByPowerOfAttorney && person.principal) {
    const principal = person.principal;
    return `Мы, ${principal.gender === 'male' ? 'гр-н РФ' : 'гр-ка РФ'} ${principal.lastName} ${principal.firstName} ${principal.middleName || ''}, ${format(new Date(principal.birthDate), 'dd MMMM yyyy', { locale: ru })} года рождения, паспорт РФ серия ${principal.passportSeries}, номер ${principal.passportNumber}, выдан ${format(new Date(principal.passportIssueDate), 'dd.MM.yyyy')}, код подразделения ${principal.passportCode}, ${principal.passportIssuer}, ${principal.gender === 'male' ? 'зарегистрированный' : 'зарегистрированная'} по адресу: ${principal.address}, ${principal.gender === 'male' ? 'именуемый' : 'именуемая'} в дальнейшем "${roleName}", в лице ${person.gender === 'male' ? 'представителя гр-на РФ' : 'представительницы гр-ки РФ'} ${person.lastName} ${person.firstName} ${person.middleName || ''}, ${format(new Date(person.birthDate), 'dd MMMM yyyy', { locale: ru })} года рождения, паспорт РФ серия ${person.passportSeries}, номер ${person.passportNumber}, выдан ${format(new Date(person.passportIssueDate), 'dd.MM.yyyy')}, код подразделения ${person.passportCode}, ${person.passportIssuer}, ${person.gender === 'male' ? 'зарегистрированного' : 'зарегистрированной'} по адресу: ${person.address}, ${person.gender === 'male' ? 'действующего' : 'действующей'} на основании доверенности № ${person.powerOfAttorney?.number} от ${format(new Date(person.powerOfAttorney?.date || ''), 'dd.MM.yyyy')}, выданной ${person.powerOfAttorney?.issuer}, с одной стороны`;
  }

  return `${person.gender === 'male' ? 'гр-н РФ' : 'гр-ка РФ'} ${person.lastName} ${person.firstName} ${person.middleName || ''}, ${format(new Date(person.birthDate), 'dd MMMM yyyy', { locale: ru })} года рождения, паспорт РФ серия ${person.passportSeries}, номер ${person.passportNumber}, выдан ${format(new Date(person.passportIssueDate), 'dd.MM.yyyy')}, код подразделения ${person.passportCode}, ${person.passportIssuer}, ${person.gender === 'male' ? 'зарегистрированный' : 'зарегистрированная'} по адресу: ${person.address}, ${person.gender === 'male' ? 'именуемый' : 'именуемая'} в дальнейшем "${roleName}", с ${role === 'seller' ? 'одной' : 'другой'} стороны`;
};

// Функция для генерации блока подписей
const getSignatureBlock = (party: Person | LegalEntity, role: 'seller' | 'buyer'): any[] => {
  const roleLabel = role === 'seller' ? 'ПРОДАВЕЦ:' : 'ПОКУПАТЕЛЬ:';
  const bottomMargin = role === 'seller' ? 20 : 0;

  if ((party as any).type === 'legal_entity') {
    const legal = party as LegalEntity;
    return [
      {
        text: roleLabel,
        style: 'subHeader',
        margin: [0, 0, 0, 10]
      },
      {
        text: legal.name,
        style: 'regularText',
        bold: true,
        margin: [0, 0, 0, 5]
      },
      {
        text: `${legal.directorPosition} ________________________________________________ / ${legal.directorName} /`,
        style: 'regularText',
        margin: [0, 0, 0, 5]
      },
      {
        text: 'М.П.',
        style: 'regularText',
        margin: [0, 10, 0, bottomMargin]
      }
    ];
  }

  const person = party as Person;
  const fio = `${person.lastName} ${person.firstName.charAt(0)}.${person.middleName ? person.middleName.charAt(0) + '.' : ''}`;

  return [
    {
      text: roleLabel,
      style: 'subHeader',
      margin: [0, 0, 0, 15]
    },
    {
      text: `________________________________________________ / ${fio} /`,
      style: 'regularText',
      margin: [0, 0, 0, 5]
    },
    {
      text: '(подпись)',
      style: 'signatureNote',
      margin: [0, 0, 0, bottomMargin]
    }
  ];
};

// Функция для генерации реквизитов стороны
const getPartyRequisites = (party: Person | LegalEntity, role: 'seller' | 'buyer'): any[] => {
  const roleLabel = role === 'seller' ? 'ПРОДАВЕЦ' : 'ПОКУПАТЕЛЬ';

  if ((party as any).type === 'legal_entity') {
    const legal = party as LegalEntity;
    return [
      {
        text: roleLabel,
        style: 'subHeader',
        margin: [0, 0, 0, 8]
      },
      {
        text: [
          { text: legal.name, bold: true },
          `\n\nИНН: ${legal.inn}\n`,
          legal.kpp ? `КПП: ${legal.kpp}\n` : '',
          `ОГРН: ${legal.ogrn}\n\n`,
          `Юридический адрес: ${legal.legalAddress}\n`,
          legal.mailingAddress && legal.mailingAddress !== legal.legalAddress
            ? `Почтовый адрес: ${legal.mailingAddress}\n`
            : '',
          `\nБанковские реквизиты:\n`,
          `Банк: ${legal.bankName}\n`,
          `БИК: ${legal.bik}\n`,
          `К/с: ${legal.correspondentAccount}\n`,
          `Р/с: ${legal.checkingAccount}\n\n`,
          legal.phone ? `Телефон: ${legal.phone}\n` : '',
          legal.email ? `Email: ${legal.email}` : ''
        ],
        style: 'compactText',
        margin: [0, 0, 0, 8]
      }
    ];
  }

  const person = party as Person;
  return [
    {
      text: roleLabel,
      style: 'subHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        { text: `${person.lastName} ${person.firstName} ${person.middleName || ''}`, bold: true },
        `\n\nПаспорт: серия ${person.passportSeries}, номер ${person.passportNumber}\n`,
        `Выдан: ${format(new Date(person.passportIssueDate), 'dd.MM.yyyy')}\n`,
        `Код подразделения: ${person.passportCode}\n`,
        `Кем выдан: ${person.passportIssuer}\n\n`,
        `Адрес регистрации: ${person.address}\n\n`,
        person.phone ? `Телефон: ${person.phone}\n` : '',
        person.cardNumber ? `Номер банковской карты: **** **** **** ${person.cardNumber.slice(-4)}\n` : '',
        person.bankName ? `\nБанковские реквизиты:\n` : '',
        person.bankName ? `Банк: ${person.bankName}\n` : '',
        person.bik ? `БИК: ${person.bik}\n` : '',
        person.correspondentAccount ? `К/с: ${person.correspondentAccount}\n` : '',
        person.checkingAccount ? `Р/с: ${person.checkingAccount}` : ''
      ].filter(Boolean),
      style: 'compactText',
      margin: [0, 0, 0, 8]
    }
  ];
};

// Функция для генерации текста способа оплаты
const getPaymentMethodText = (contract: Contract): string => {
  const paymentMethod = contract.paymentMethod || 'bank_transfer';

  switch (paymentMethod) {
    case 'cash':
      return 'Расчет производится наличными средствами.';

    case 'card_transfer':
      return 'Расчет производится путем перевода денежных средств на банковскую карту Продавца.';

    case 'mixed':
      const cashAmount = Number(contract.cashAmount || 0);
      const transferAmount = Number(contract.transferAmount || 0);
      return `Расчет производится смешанным способом: ${cashAmount.toLocaleString('ru-RU')} (${numberToWords(cashAmount)}) рублей наличными средствами, ${transferAmount.toLocaleString('ru-RU')} (${numberToWords(transferAmount)}) рублей безналичным переводом на расчетный счет Продавца, указанный в разделе "Реквизиты и адреса сторон".`;

    case 'bank_transfer':
    default:
      return 'Расчет производится путем безналичного перевода на расчетный счет Продавца, указанный в разделе "Реквизиты и адреса сторон".';
  }
};

export const printContractPDF = (contract: Contract) => {
  const { docDefinition, fileName } = createDocumentDefinition(contract);
  printPDFOnMobile(docDefinition, fileName);
};

// Функция для создания предварительного договора
const createPreliminaryContractDefinition = (contract: Contract) => {
  const content: any[] = [
    // Заголовок
    {
      text: contract.contractNumber
        ? `ПРЕДВАРИТЕЛЬНЫЙ ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА № ${contract.contractNumber}`
        : 'ПРЕДВАРИТЕЛЬНЫЙ ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА',
      style: 'title',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    },

    // Место и дата
    {
      columns: [
        {
          width: '50%',
          text: `г. ${contract.dealCity}`,
          style: 'datePlace',
          alignment: 'left'
        },
        {
          width: '50%',
          text: `${format(new Date(contract.dealDate), 'dd MMMM yyyy', { locale: ru })}`,
          style: 'datePlace',
          alignment: 'right'
        }
      ],
      margin: [0, 0, 0, 10]
    },

    // Стороны договора
    {
      text: [
        // Продавец
        getPartyDescription(contract.seller, 'seller') + ' и\n\n',
        // Покупатель
        getPartyDescription(contract.buyer, 'buyer') + ', заключили настоящий предварительный договор о нижеследующем:'
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 1. Предмет договора
    {
      text: '1. ПРЕДМЕТ ДОГОВОРА',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `1.1. Настоящий предварительный договор заключен в соответствии со статьей 429 Гражданского кодекса Российской Федерации.\n\n`,
        `1.2. Стороны обязуются в срок до ${contract.mainContractDeadline ? format(new Date(contract.mainContractDeadline), 'dd MMMM yyyy', { locale: ru }) : 'указанной даты'} года заключить основной договор купли-продажи земельного участка (далее – Основной договор) на условиях, предусмотренных настоящим предварительным договором.\n\n`,
        `1.3. Предметом Основного договора будет являться земельный участок (далее – Участок) со следующими характеристиками:\n`,
        `- площадь: ${contract.landPlot.area} кв.м\n`,
        `- кадастровый номер: ${contract.landPlot.cadastralNumber}\n`,
        `- категория земель: ${contract.landPlot.category}\n`,
        `- разрешенное использование: ${contract.landPlot.purpose}\n`,
        `- адрес (местоположение): ${contract.landPlot.address}\n`,
        `- вид права: ${contract.landPlot.rightType}\n`,
        `- номер регистрации права: ${contract.landPlot.rightRegistrationNumber}\n`,
        `- дата регистрации права: ${format(new Date(contract.landPlot.rightRegistrationDate), 'dd.MM.yyyy')}\n`,
        `- основание государственной регистрации: ${contract.landPlot.registrationBasis}\n\n`,
        `1.4. Границы Участка определены в соответствии с данными государственного кадастра недвижимости.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 2. Цена и порядок расчетов
    {
      text: '2. ЦЕНА И ПОРЯДОК РАСЧЕТОВ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `2.1. Стоимость земельного участка по Основному договору составляет денежную сумму в размере ${Number(contract.amount).toLocaleString('ru-RU')} (${numberToWords(Number(contract.amount))}) рублей. Указанная цена установлена по соглашению Сторон настоящего предварительного договора, является окончательной и изменению не подлежит.\n\n`,
        contract.deposit && Number(contract.deposit) > 0
          ? `2.2. При подписании настоящего предварительного договора Покупатель уплачивает Продавцу ${contract.depositType === 'deposit' ? 'задаток' : 'аванс'} в размере ${Number(contract.deposit).toLocaleString('ru-RU')} (${numberToWords(Number(contract.deposit))}) рублей.\n\n`
          : `2.2. При подписании настоящего предварительного договора задаток (аванс) не уплачивается.\n\n`,
        contract.deposit && Number(contract.deposit) > 0 && contract.depositType === 'deposit'
          ? `2.3. В случае неисполнения обязательства по заключению Основного договора по вине Покупателя задаток остается у Продавца. В случае неисполнения обязательства по заключению Основного договора по вине Продавца последний обязан уплатить Покупателю двойную сумму задатка, а также возместить убытки, причиненные неисполнением обязательства, с зачетом суммы задатка.\n\n`
          : contract.deposit && Number(contract.deposit) > 0 && contract.depositType === 'advance'
            ? `2.3. Аванс, уплаченный Покупателем, засчитывается в счет оплаты по Основному договору. В случае неисполнения обязательства по заключению Основного договора аванс подлежит возврату Покупателю.\n\n`
            : '',
        `2.${contract.deposit && Number(contract.deposit) > 0 ? '4' : '3'}. Оставшаяся часть цены в размере ${Number(Number(contract.amount) - (Number(contract.deposit || 0))).toLocaleString('ru-RU')} (${numberToWords(Number(Number(contract.amount) - (Number(contract.deposit || 0))))}) рублей уплачивается Покупателем Продавцу при заключении Основного договора. ${getPaymentMethodText(contract)}\n\n`,
        `2.${contract.deposit && Number(contract.deposit) > 0 ? '5' : '4'}. Расходы по государственной регистрации перехода права собственности возлагаются на Покупателя.`
      ].filter(Boolean),
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 3. Обязательства сторон
    {
      text: '3. ОБЯЗАТЕЛЬСТВА СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `3.1. Продавец обязуется:\n`,
        `3.1.1. Заключить с Покупателем Основной договор купли-продажи земельного участка на условиях, предусмотренных настоящим предварительным договором, в срок до ${contract.mainContractDeadline ? format(new Date(contract.mainContractDeadline), 'dd MMMM yyyy', { locale: ru }) : 'указанной даты'} года.\n`,
        `3.1.2. Не совершать действий, препятствующих Покупателю в приобретении права собственности на Участок.\n`,
        `3.1.3. Предоставить Покупателю все необходимые документы для заключения Основного договора.\n`,
        `3.1.4. Не заключать договор купли-продажи Участка с третьими лицами до истечения срока, указанного в пункте 3.1.1 настоящего предварительного договора.\n\n`,
        `3.2. Покупатель обязуется:\n`,
        `3.2.1. Заключить с Продавцом Основной договор купли-продажи земельного участка на условиях, предусмотренных настоящим предварительным договором, в срок до ${contract.mainContractDeadline ? format(new Date(contract.mainContractDeadline), 'dd MMMM yyyy', { locale: ru }) : 'указанной даты'} года.\n`,
        `3.2.2. Уплатить Продавцу оставшуюся часть цены земельного участка при заключении Основного договора.\n`,
        `3.2.3. Подать документы для государственной регистрации перехода права собственности на Участок.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 4. Ответственность сторон
    {
      text: '4. ОТВЕТСТВЕННОСТЬ СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `4.1. В случае уклонения одной из Сторон от заключения Основного договора другая Сторона вправе обратиться в суд с требованием о понуждении заключить договор, а также о возмещении убытков, причиненных уклонением от заключения договора.\n\n`,
        `4.2. За неисполнение или ненадлежащее исполнение обязательств по настоящему предварительному договору стороны несут ответственность в соответствии с действующим законодательством Российской Федерации.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 5. Разрешение споров
    {
      text: '5. РАЗРЕШЕНИЕ СПОРОВ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `5.1. Все споры, разногласия или требования, возникающие из настоящего предварительного договора или в связи с ним, подлежат разрешению путем переговоров.\n\n`,
        `5.2. В случае невозможности разрешения споров путем переговоров они подлежат рассмотрению в суде по месту нахождения Участка в соответствии с действующим законодательством Российской Федерации.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 6. Заключительные положения
    {
      text: '6. ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `6.1. Настоящий предварительный договор вступает в силу с момента его подписания Сторонами.\n\n`,
        `6.2. Договор действует до заключения Основного договора или до истечения срока, указанного в п. 1.2.\n\n`,
        `6.3. Порядок и обязательность заключения основного договора (ст.ст. 429, 380, 381 ГК РФ) сторонам разъяснены.\n\n`,
        `6.4. Ответственность и права сторон, не предусмотренные в настоящем предварительном договоре, определяются в соответствии с законодательством Российской Федерации.\n\n`,
        `6.5. Настоящий предварительный договор составлен и подписан в двух экземплярах, имеющих равную юридическую силу.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 10]
    }
  ];

  // Добавляем дополнительные условия если есть
  if (contract.additionalConditions && contract.additionalConditions.trim()) {
    content.push(
      {
        text: '7. ДОПОЛНИТЕЛЬНЫЕ УСЛОВИЯ',
        style: 'sectionHeader',
        margin: [0, 0, 0, 5]
      },
      {
        text: contract.additionalConditions,
        style: 'regularText',
        margin: [0, 0, 0, 10]
      }
    );
  }

  // Добавляем реквизиты и адреса сторон
  content.push(
    {
      text: contract.additionalConditions && contract.additionalConditions.trim() ? '8. РЕКВИЗИТЫ И АДРЕСА СТОРОН' : '7. РЕКВИЗИТЫ И АДРЕСА СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 10]
    },
    {
      columns: [
        {
          width: '48%',
          stack: getPartyRequisites(contract.seller, 'seller')
        },
        {
          width: '4%',
          text: ''
        },
        {
          width: '48%',
          stack: getPartyRequisites(contract.buyer, 'buyer')
        }
      ],
      columnGap: 10
    }
  );

  // Добавляем реквизиты и подписи сторон
  content.push(
    {
      text: contract.additionalConditions && contract.additionalConditions.trim() ? '9. ПОДПИСИ СТОРОН' : '8. ПОДПИСИ СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 10]
    },
    ...getSignatureBlock(contract.seller, 'seller'),
    ...getSignatureBlock(contract.buyer, 'buyer')
  );

  // Определяем документ с компактным форматированием
  const docDefinition: any = {
    content: content,

    // Компактные стили для размещения на 2 страницах
    styles: {
      title: {
        fontSize: 11,
        bold: true,
        alignment: 'center'
      },
      sectionHeader: {
        fontSize: 10,
        bold: true
      },
      subHeader: {
        fontSize: 9,
        bold: true
      },
      datePlace: {
        fontSize: 9,
        italics: true
      },
      regularText: {
        fontSize: 9,
        lineHeight: 1.1
      },
      compactText: {
        fontSize: 8,
        lineHeight: 1.0
      },
      signature: {
        fontSize: 9
      },
      signatureNote: {
        fontSize: 7,
        italics: true
      }
    },

    // Компактные настройки документа
    pageSize: 'A4',
    pageMargins: [25, 20, 20, 20],
    defaultStyle: {
      fontSize: 9,
      lineHeight: 1.1
    },

    // Нумерация страниц
    header: function (currentPage: number, pageCount: number) {
      if (currentPage > 1) {
        return {
          text: currentPage.toString(),
          alignment: 'center',
          margin: [0, 8, 0, 0],
          fontSize: 10
        };
      }
      return null;
    }
  };

  // Генерируем имя файла
  const fileName = `Предварительный_договор_${contract.landPlot.cadastralNumber}_${format(new Date(), 'dd-MM-yyyy')}.pdf`;

  return { docDefinition, fileName };
};

// Вспомогательная функция для создания определения документа
const createDocumentDefinition = (contract: Contract) => {
  // Для предварительного договора используем отдельную структуру
  if (contract.type === 'preliminary') {
    return createPreliminaryContractDefinition(contract);
  }

  // Определяем базовое содержимое документа для основного договора и договора с рассрочкой
  const content: any[] = [
    // Заголовок
    {
      text: contract.type === 'main'
        ? (contract.contractNumber
          ? `ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА № ${contract.contractNumber}`
          : 'ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА')
        : (contract.contractNumber
          ? `ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА С РАССРОЧКОЙ ПЛАТЕЖА № ${contract.contractNumber}`
          : 'ДОГОВОР КУПЛИ-ПРОДАЖИ ЗЕМЕЛЬНОГО УЧАСТКА С РАССРОЧКОЙ ПЛАТЕЖА'),
      style: 'title',
      alignment: 'center',
      margin: [0, 0, 0, 10]
    },

    // Место и дата
    {
      columns: [
        {
          width: '50%',
          text: `г. ${contract.dealCity}`,
          style: 'datePlace',
          alignment: 'left'
        },
        {
          width: '50%',
          text: `${format(new Date(contract.dealDate), 'dd MMMM yyyy', { locale: ru })}`,
          style: 'datePlace',
          alignment: 'right'
        }
      ],
      margin: [0, 0, 0, 10]
    },

    // Стороны договора в новом формате
    {
      text: [
        // Продавец
        getPartyDescription(contract.seller, 'seller') + ' и\n\n',
        // Покупатель
        getPartyDescription(contract.buyer, 'buyer') + ', заключили настоящий договор о нижеследующем:'
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 1. Предмет договора
    {
      text: '1. ПРЕДМЕТ ДОГОВОРА',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `1.1. Продавец обязуется передать в собственность Покупателя земельный участок (далее – Участок), а Покупатель обязуется принять Участок и уплатить за него денежную сумму (цену), предусмотренную настоящим договором.\n\n`,
        `1.2. Участок имеет следующие характеристики:\n`,
        `- площадь: ${contract.landPlot.area} кв.м\n`,
        `- кадастровый номер: ${contract.landPlot.cadastralNumber}\n`,
        `- категория земель: ${contract.landPlot.category}\n`,
        `- разрешенное использование: ${contract.landPlot.purpose}\n`,
        `- адрес (местоположение): ${contract.landPlot.address}\n`,
        `- вид права: ${contract.landPlot.rightType}\n`,
        `- номер регистрации права: ${contract.landPlot.rightRegistrationNumber}\n`,
        `- дата регистрации права: ${format(new Date(contract.landPlot.rightRegistrationDate), 'dd.MM.yyyy')}\n`,
        `- основание государственной регистрации: ${contract.landPlot.registrationBasis}\n\n`,
        `1.3. Границы Участка определены в соответствии с данными государственного кадастра недвижимости.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 2. Цена и порядок расчетов
    {
      text: '2. ЦЕНА И ПОРЯДОК РАСЧЕТОВ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    ...(contract.type === 'installment' ? [
      {
        text: [
          `2.1. Стоимость земельного участка составляет денежную сумму в размере ${Number(contract.amount).toLocaleString('ru-RU')} (${numberToWords(Number(contract.amount))}) рублей. Указанная цена установлена по соглашению Сторон настоящего договора, является окончательной и изменению не подлежит.\n`,
          contract.initialPayment && Number(contract.initialPayment) > 0
            ? `2.2. При подписании настоящего договора Покупатель уплачивает Продавцу первоначальный взнос в размере ${Number(contract.initialPayment).toLocaleString('ru-RU')} (${numberToWords(Number(contract.initialPayment))}) рублей.\n`
            : `2.2. При подписании настоящего договора первоначальный взнос не уплачивается.\n`,
          `2.3. Оставшаяся часть цены в размере ${Number(Number(contract.amount) - (Number(contract.initialPayment || 0))).toLocaleString('ru-RU')} (${numberToWords(Number(Number(contract.amount) - (Number(contract.initialPayment || 0))))}) рублей уплачивается Покупателем Продавцу в соответствии с графиком платежей, указанным в пункте 2.4 настоящего договора. ${contract.paymentMethod && contract.paymentMethod !== 'bank_transfer' ? getPaymentMethodText(contract) : ''}\n`,
          contract.installmentPayments && contract.installmentPayments.length > 0
            ? `2.4. График платежей:\n${contract.installmentPayments.map((payment, index) => {
              const paymentAmount = Number(payment.amount);
              return `   ${index + 1}) Сумма ${paymentAmount.toLocaleString('ru-RU')} (${numberToWords(paymentAmount)}) рублей - срок оплаты до ${format(new Date(payment.dueDate), 'dd.MM.yyyy')}${payment.description ? ` (${payment.description})` : ''}`;
            }).join('\n')}\n`
            : `2.4. График платежей устанавливается дополнительным соглашением Сторон.\n`,
          contract.latePaymentPenalty
            ? `2.5. В случае просрочки платежа Покупатель уплачивает Продавцу штрафные санкции в следующем размере: ${contract.latePaymentPenalty}.\n`
            : `2.5. В случае просрочки платежа Покупатель уплачивает Продавцу неустойку в размере 0,1% от суммы просроченного платежа за каждый день просрочки.\n`,
          contract.hasEncumbrance
            ? `2.6. До полной оплаты цены земельного участка на него устанавливается обременение в виде залога в пользу Продавца. Обременение регистрируется в органах государственной регистрации прав на недвижимое имущество одновременно с регистрацией перехода права собственности. Обременение снимается после полной оплаты цены земельного участка на основании заявления Продавца.\n`
            : `2.6. Обременение на земельный участок до полной оплаты не устанавливается.\n`,
          `2.7. Расходы по государственной регистрации перехода права собственности и обременения (при его наличии) возлагаются на Покупателя.`
        ],
        style: 'regularText',
        margin: [0, 0, 0, 8]
      }
    ] : [
      {
        text: [
          `2.1. Стоимость земельного участка составляет денежную сумму в размере ${Number(contract.amount).toLocaleString('ru-RU')} (${numberToWords(Number(contract.amount))}) рублей. Указанная цена установлена по соглашению Сторон настоящего договора, является окончательной и изменению не подлежит.\n`,
          `2.2. Расчет между сторонами произведен полностью до подписания настоящего договора. ${getPaymentMethodText(contract)}\n`,
          `2.3. Расходы по государственной регистрации перехода права собственности возлагаются на Покупателя.`
        ],
        style: 'regularText',
        margin: [0, 0, 0, 8]
      }
    ]),

    // 3. Права и обязанности сторон
    {
      text: '3. ПРАВА И ОБЯЗАННОСТИ СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `3.1. Продавец обязуется:\n`,
        `3.1.1. Передать Покупателю Участок, свободный от прав третьих лиц, не обремененный залогом, не состоящий под арестом или запрещением и не являющийся предметом спора.\n`,
        `3.1.2. Предоставить документы, необходимые для государственной регистрации перехода права собственности.\n`,
        `3.1.3. Не совершать действий, препятствующих Покупателю в приобретении права собственности на Участок.\n\n`,
        `3.2. Покупатель обязуется:\n`,
        `3.2.1. Принять Участок в порядке и в сроки, установленные договором.\n`,
        `3.2.2. Подать документы для государственной регистрации перехода права собственности.\n`,
        `3.2.3. Нести расходы по содержанию Участка с момента перехода права собственности.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 4. Переход права собственности и передача участка
    {
      text: '4. ПЕРЕХОД ПРАВА СОБСТВЕННОСТИ И ПЕРЕДАЧА УЧАСТКА',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `4.1. Право собственности на Участок переходит к Покупателю с момента государственной регистрации перехода права собственности в органах государственной регистрации прав на недвижимое имущество и сделок с ним.\n`,
        `4.2. Передача Участка осуществляется одновременно с подписанием настоящего договора. Покупатель считается принявшим Участок в том состоянии, в котором он находится на момент заключения договора.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 5. Обременения и ограничения
    {
      text: '5. ОБРЕМЕНЕНИЯ И ОГРАНИЧЕНИЯ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        contract.type === 'installment' && contract.hasEncumbrance
          ? `5.1. На момент подписания настоящего договора Участок правами других лиц не обременен, за исключением обременения в виде залога в пользу Продавца, которое устанавливается в соответствии с пунктом 2.6 настоящего договора.\n`
          : `5.1. Участок правами других лиц не обременен.\n`,
        `5.2. Ограничения в пользовании участка по сведениям ЕГРН: указанные в технической документации кадастра недвижимости.\n`,
        `5.3. Продавец гарантирует, что до подписания настоящего договора Участок никому не продан, не заложен, не передан в аренду, наем, безвозмездное пользование, какими-либо иными обязательствами не обременен, а также, что Участок в споре и под арестом (запрещением) не состоит, свободен от любых прав и притязаний третьих лиц.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 6. Заверения и гарантии
    {
      text: '6. ЗАВЕРЕНИЯ И ГАРАНТИИ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `6.1. По настоящему договору Стороны подтверждают, что не лишены дееспособности, не состоят под опекой и попечительством, не страдают заболеваниями, препятствующими осознать суть договора, а также отсутствуют обстоятельства, вынуждающие совершить данный договор на крайне невыгодных для себя условиях.\n`,
        `6.2. Покупатель осмотрел участок, ознакомлен с его характеристиками и текущим состоянием, претензий не имеет.\n`,
        `6.3. Настоящий договор содержит все соглашения сторон по его предмету и отменяет все предыдущие договоренности.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 7. Ответственность сторон
    {
      text: '7. ОТВЕТСТВЕННОСТЬ СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `7.1. За неисполнение или ненадлежащее исполнение обязательств по настоящему договору стороны несут ответственность в соответствии с действующим законодательством Российской Федерации.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 8. Разрешение споров
    {
      text: '8. РАЗРЕШЕНИЕ СПОРОВ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `8.1. Все споры, разногласия или требования, возникающие из настоящего договора или в связи с ним, подлежат разрешению путем переговоров.\n\n`,
        `8.2. В случае невозможности разрешения споров путем переговоров они подлежат рассмотрению в суде по месту нахождения Участка в соответствии с действующим законодательством Российской Федерации.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 8]
    },

    // 9. Заключительные положения
    {
      text: '9. ЗАКЛЮЧИТЕЛЬНЫЕ ПОЛОЖЕНИЯ',
      style: 'sectionHeader',
      margin: [0, 0, 0, 5]
    },
    {
      text: [
        `9.1. В соответствии со ст. 223, 251, 551 ГК РФ Покупатель приобретает право собственности на земельный участок с момента государственной регистрации перехода права собственности в Управлении Федеральной службы регистрации, кадастра и картографии.\n\n`,
        `9.2. «Покупатель» оплачивает все расходы, связанные с государственной регистрацией перехода права собственности на земельный участок.\n\n`,
        `9.3. В соответствии со статьей 556 Гражданского кодекса Российской Федерации настоящий договор имеет силу передаточного акта.\n\n`,
        `9.4. Содержание ст.ст. 131, 165, 167, 170, 209, 223, 260, 488, 551, 556 ГК РФ и соответствующих статей ФЗ «О государственной регистрации прав на недвижимое имущество и сделок с ним» в части порядка и обязательности государственной регистрации возникновения, перехода и прекращения прав на указанное имущество сторонам разъяснено.\n\n`,
        `9.5. Ответственность и права сторон, не предусмотренные в настоящем договоре, определяются в соответствии с законодательством Российской Федерации.\n\n`,
        `9.6. Настоящий Договор составлен и подписан в двух экземплярах, имеющих равную юридическую силу.`
      ],
      style: 'regularText',
      margin: [0, 0, 0, 10]
    }
  ];

  // Добавляем дополнительные условия если есть
  if (contract.additionalConditions && contract.additionalConditions.trim()) {
    content.push(
      {
        text: '10. ДОПОЛНИТЕЛЬНЫЕ УСЛОВИЯ',
        style: 'sectionHeader',
        margin: [0, 0, 0, 5]
      },
      {
        text: contract.additionalConditions,
        style: 'regularText',
        margin: [0, 0, 0, 10]
      }
    );
  }

  // Добавляем реквизиты и адреса сторон
  content.push(
    {
      text: contract.additionalConditions && contract.additionalConditions.trim() ? '11. РЕКВИЗИТЫ И АДРЕСА СТОРОН' : '10. РЕКВИЗИТЫ И АДРЕСА СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 10]
    },
    {
      columns: [
        {
          width: '48%',
          stack: getPartyRequisites(contract.seller, 'seller')
        },
        {
          width: '4%',
          text: ''
        },
        {
          width: '48%',
          stack: getPartyRequisites(contract.buyer, 'buyer')
        }
      ],
      columnGap: 10
    }
  );

  // Добавляем реквизиты и подписи сторон
  content.push(
    {
      text: contract.additionalConditions && contract.additionalConditions.trim() ? '12. ПОДПИСИ СТОРОН' : '11. ПОДПИСИ СТОРОН',
      style: 'sectionHeader',
      margin: [0, 0, 0, 10]
    },
    ...getSignatureBlock(contract.seller, 'seller'),
    ...getSignatureBlock(contract.buyer, 'buyer')
  );

  // Определяем документ с компактным форматированием
  const docDefinition: any = {
    content: content,

    // Компактные стили для размещения на 2 страницах
    styles: {
      title: {
        fontSize: 11,
        bold: true,
        alignment: 'center'
      },
      sectionHeader: {
        fontSize: 10,
        bold: true
      },
      subHeader: {
        fontSize: 9,
        bold: true
      },
      datePlace: {
        fontSize: 9,
        italics: true
      },
      regularText: {
        fontSize: 9,
        lineHeight: 1.1
      },
      compactText: {
        fontSize: 8,
        lineHeight: 1.0
      },
      signature: {
        fontSize: 9
      },
      signatureNote: {
        fontSize: 7,
        italics: true
      }
    },

    // Компактные настройки документа
    pageSize: 'A4',
    pageMargins: [25, 20, 20, 20], // комфортные поля для чтения
    defaultStyle: {
      fontSize: 9,
      lineHeight: 1.1
    },

    // Нумерация страниц
    header: function (currentPage: number, pageCount: number) {
      if (currentPage > 1) {
        return {
          text: currentPage.toString(),
          alignment: 'center',
          margin: [0, 8, 0, 0],
          fontSize: 10
        };
      }
      return null;
    }
  };

  // Генерируем имя файла
  const fileName = contract.type === 'main'
    ? `Договор_${contract.landPlot.cadastralNumber}_${format(new Date(), 'dd-MM-yyyy')}.pdf`
    : contract.type === 'installment'
      ? `Договор_с_рассрочкой_${contract.landPlot.cadastralNumber}_${format(new Date(), 'dd-MM-yyyy')}.pdf`
      : `Предварительный_договор_${contract.landPlot.cadastralNumber}_${format(new Date(), 'dd-MM-yyyy')}.pdf`;

  return { docDefinition, fileName };
};

// Основная функция генерации PDF (автоматическая печать для всех устройств)
export const generateContractPDF = (contract: Contract) => {
  const { docDefinition, fileName } = createDocumentDefinition(contract);

  if (isMobileDevice()) {
    // Для мобильных устройств используем специальную логику
    printPDFOnMobile(docDefinition, fileName);
  } else {
    // Для десктопных устройств создаем PDF и открываем диалог печати
    const pdfDocGenerator = (pdfMake as any).createPdf(docDefinition);
    pdfDocGenerator.getBlob((blob: Blob) => {
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');

      if (printWindow) {
        printWindow.addEventListener('load', () => {
          // Небольшая задержка для полной загрузки PDF
          setTimeout(() => {
            printWindow.print();
          }, 500);
        });

        // Fallback для случаев когда load событие не сработает
        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            printWindow.print();
          }
        }, 1000);
      } else {
        // Если окно не открылось, показываем уведомление и скачиваем
        window.alert('Для печати разрешите всплывающие окна в браузере');
        pdfDocGenerator.download(fileName);
      }
    });
  }
};

// Функция для перевода чисел в слова
const numberToWords = (num: number): string => {
  if (num === 0) return 'ноль';

  const ones = ['', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять'];
  const tens = ['', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят', 'восемьдесят', 'девяносто'];
  const hundreds = ['', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот', 'восемьсот', 'девятьсот'];
  const teens = ['десять', 'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать', 'семнадцать', 'восемнадцать', 'девятнадцать'];

  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return hundreds[Math.floor(num / 100)] + (num % 100 ? ' ' + numberToWords(num % 100) : '');

  if (num < 1000000) {
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    let result = numberToWords(thousands);

    if (thousands % 10 === 1 && thousands % 100 !== 11) {
      result += ' тысяча';
    } else if ([2, 3, 4].includes(thousands % 10) && ![12, 13, 14].includes(thousands % 100)) {
      result += ' тысячи';
    } else {
      result += ' тысяч';
    }

    if (remainder > 0) {
      result += ' ' + numberToWords(remainder);
    }

    return result;
  }

  return num.toLocaleString('ru-RU');
};